/**
 * Route type detection utilities for multi-chain vs chain-specific pages.
 *
 * Multi-chain pages: /vaults, /variable, /portfolio, /curator/[id]
 * Chain-specific pages: /[chain]/vault/..., /[chain]/variable/...
 */

export type RouteType = 'multi-chain' | 'chain-specific'

// Also duplicated in src/proxy.ts (Edge runtime — cannot import from here)
export const MULTI_CHAIN_SECTIONS = new Set(['vaults', 'variable', 'portfolio', 'curator'])

/**
 * Determine whether the current page is multi-chain or chain-specific.
 * @param pathname - The URL pathname (e.g., "/vaults", "/ethereum/vault/0x...")
 */
export function getRouteType(pathname: string): RouteType {
  const firstSegment = pathname.split('/').filter(Boolean)[0]
  if (firstSegment && MULTI_CHAIN_SECTIONS.has(firstSegment)) {
    return 'multi-chain'
  }
  return 'chain-specific'
}

/**
 * Check if a pathname segment is a multi-chain section name.
 */
export function isMultiChainSection(segment: string): boolean {
  return MULTI_CHAIN_SECTIONS.has(segment)
}

/**
 * Extract the section name from a pathname regardless of URL shape.
 *
 * Multi-chain: "/vaults" → "vaults", "/variable" → "variable"
 * Chain-specific: "/ethereum/vault/0x..." → "vault", "/base/borrow" → "borrow"
 * @internal
 */
export function getSectionFromPathname(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  const firstSegment = segments[0]

  if (firstSegment && MULTI_CHAIN_SECTIONS.has(firstSegment)) {
    return firstSegment
  }

  // Chain-specific: section is at index 1 (e.g., /ethereum/vault/...)
  return segments[1] ?? ''
}
